/**
 * FERP Portal - UI Schema List Page
 *
 * UI Schemas define entity-specific form layouts, field visibility, and widget
 * selection for the FormRenderer pipeline (DRR-055). Loaded from YAML configs
 * and served via the form_schema_api.
 *
 * NOTE: Backend has listUiSchemas and uiSchema queries but they are part of the
 * FormSchema pipeline (not a separate config browser). This page shows the known
 * seeded UI schema configurations.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend: displays known config metadata
 * - Admin view layer only
 */

import {
  Alert,
  Anchor,
  Badge,
  Card,
  Container,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { IconForms, IconInfoCircle, IconLayout } from '@tabler/icons-react';

const KNOWN_UI_SCHEMAS = [
  {
    id: 'core-actor-form',
    entity: 'Actor',
    subgraph: 'core',
    modes: ['CREATE', 'EDIT'],
    description: 'Form layout for Actor entity (all actor types: Human, AI, System, Composite).',
  },
  {
    id: 'erp-gl-account-form',
    entity: 'ErpGlAccount',
    subgraph: 'erp',
    modes: ['CREATE', 'EDIT', 'VIEW'],
    description: 'Form layout for ERP General Ledger accounts with complex field types (DRR-060).',
  },
  {
    id: 'fhir-patient-form',
    entity: 'FhirPatient',
    subgraph: 'fhir',
    modes: ['CREATE', 'EDIT', 'VIEW'],
    description: 'Form layout for FHIR Patient entity with healthcare-specific field types.',
  },
  {
    id: 'payroll-run-form',
    entity: 'PayrollRun',
    subgraph: 'payroll',
    modes: ['CREATE', 'EDIT', 'APPROVE'],
    description: 'Form layout for payroll run processing with dual-approval support.',
  },
];

const SUBGRAPH_COLORS: Record<string, string> = {
  core: 'blue',
  erp: 'orange',
  fhir: 'cyan',
  payroll: 'teal',
};

export function UiSchemaListPage() {
  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>UI Schemas</Title>
            <Text c="dimmed" size="sm">
              Entity-specific form layouts, field visibility, and widget selection (DRR-055)
            </Text>
          </div>
          <Badge variant="light" color="indigo" size="lg">
            {KNOWN_UI_SCHEMAS.length} Schemas
          </Badge>
        </Group>

        <Alert icon={<IconInfoCircle size={16} />} color="blue" title="FormSchema Pipeline">
          UI Schemas are consumed by the FormRenderer pipeline: Backend FormSchema → GraphQL
          formSchema query → useFormSchema hook → schemaToFormDefinition → FormRenderer.
          This page shows seeded base schemas. Tenant-specific overrides are managed via
          Form Schema Overrides (fso-*).
        </Alert>

        <Card withBorder p="md">
          <Group gap="sm" mb="xs">
            <ThemeIcon variant="light" color="indigo">
              <IconLayout size={18} />
            </ThemeIcon>
            <Text fw={600}>How UI Schemas Work</Text>
          </Group>
          <Text size="sm" c="dimmed">
            YAML config → DDB (FormSchema record) → GraphQL formSchema(subgraph, entityType) query →{' '}
            <Text span ff="monospace" size="sm">useFormSchema()</Text> hook →{' '}
            <Text span ff="monospace" size="sm">schemaToFormDefinition()</Text> →{' '}
            <Text span ff="monospace" size="sm">FormRenderer</Text> (28+ field types, conditions, validation).
            LLM editor available for schema authoring.
          </Text>
        </Card>

        <Stack gap="sm">
          {KNOWN_UI_SCHEMAS.map((schema) => (
            <Card key={schema.id} withBorder p="md">
              <Group justify="space-between" mb="xs">
                <Group gap="sm">
                  <ThemeIcon variant="light" color={SUBGRAPH_COLORS[schema.subgraph] ?? 'gray'} size="sm">
                    <IconForms size={14} />
                  </ThemeIcon>
                  <div>
                    <Text fw={500}>{schema.entity}</Text>
                    <Text size="xs" c="dimmed" ff="monospace">{schema.id}</Text>
                  </div>
                </Group>
                <Badge variant="light" color={SUBGRAPH_COLORS[schema.subgraph] ?? 'gray'} size="sm">
                  {schema.subgraph}
                </Badge>
              </Group>

              <Text size="sm" c="dimmed" mb="sm">{schema.description}</Text>

              <SimpleGrid cols={2} spacing="sm">
                <div>
                  <Text size="xs" c="dimmed">Modes</Text>
                  <Group gap={4}>
                    {schema.modes.map((mode) => (
                      <Badge key={mode} variant="outline" size="xs">{mode}</Badge>
                    ))}
                  </Group>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Subgraph</Text>
                  <Text size="sm" ff="monospace">{schema.subgraph}</Text>
                </div>
              </SimpleGrid>
            </Card>
          ))}
        </Stack>

        <Text size="xs" c="dimmed">
          To add or modify UI schemas, edit{' '}
          <Anchor size="xs" href="#" onClick={(e) => e.preventDefault()}>
            common/configs/seed/ui-schemas/*.yaml
          </Anchor>{' '}
          and re-seed. LLM editor: /ferp-core/forms → Schema Editor.
        </Text>
      </Stack>
    </Container>
  );
}

export default UiSchemaListPage;
